import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const SaasRevenueAnalytics = ({ data, selectedPeriod }) => {
  const COLORS = ['var(--color-primary)', 'var(--color-accent)', 'var(--color-success)', 'var(--color-warning)'];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-gradient-to-br from-success/10 to-success/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="DollarSign" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Recurring Revenue</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.totalSubscriptionRevenue / 1000)?.toFixed(1)}K
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="CreditCard" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Subscriptions</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                {data?.activeSubscriptions}
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-accent/10 to-accent/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Subscription Value</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.avgSubscriptionValue / 1000)?.toFixed(1)}K
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-warning/10 to-warning/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
              <Icon name="Users" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Customer Lifetime Value</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.lifetimeValue / 1000)?.toFixed(1)}K
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
            Subscription Revenue Growth
          </h3>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.revenueByMonth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" stroke="var(--color-muted-foreground)" style={{ fontSize: '14px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '14px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    padding: '12px'
                  }}
                  formatter={(value) => `$${value?.toLocaleString()}`}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--color-success)"
                  strokeWidth={3}
                  name="Revenue"
                  dot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="subscriptions"
                  stroke="var(--color-primary)"
                  strokeWidth={3}
                  name="Subscriptions"
                  dot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
            Churn Analysis
          </h3>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Current Churn Rate</span>
              <span className="text-2xl font-heading font-semibold text-error">
                {data?.churnAnalysis?.churnRate}%
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Info" size={12} />
              <span>{data?.churnedThisMonth} churned, {data?.newSubscriptionsThisMonth} new this month</span>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground mb-3">Top Churn Reasons</h4>
            {data?.churnAnalysis?.topReasons?.map((reason, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-foreground">{reason?.reason}</span>
                  <span className="text-sm font-medium text-foreground">{reason?.percentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-error rounded-full h-2 transition-all duration-500"
                    style={{ width: `${reason?.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Subscription Tier Breakdown
        </h3>
        <div className="space-y-4">
          {data?.subscriptionTiers?.map((tier, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${COLORS?.[index]}15` }}
                    >
                      <Icon name="Award" size={20} color={COLORS?.[index]} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground">{tier?.tier}</h4>
                      <p className="text-sm text-muted-foreground">{tier?.features}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Subscriptions</p>
                    <p className="text-2xl font-heading font-semibold text-foreground">{tier?.count}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Monthly Revenue</p>
                    <p className="text-2xl font-heading font-semibold text-success">
                      ${tier?.monthlyRevenue?.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Price</p>
                    <p className="text-2xl font-heading font-semibold text-foreground">
                      ${tier?.price}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SaasRevenueAnalytics;